/**
 * Phase 20.1 — QuoteBasisEditor
 *
 * Post-creation edit-UI voor offertes met `quote_basis` (Phase 20):
 *   * total_volume  — m³ × tarief flow
 *   * lines         — meerdere regels/items flow
 *   * fixed         — vaste prijs / service flow
 *
 * Legacy quotes (zonder quote_basis) krijgen een migratiebanner — admin kan
 * met één klik converteren naar één van de drie modi, of de bestaande
 * "Prijsberekening" platte numField-grid blijven gebruiken (geleverd door
 * de parent als fallback).
 *
 * Stability requirements (R4):
 *   - All sub-components at MODULE level (no inline functions during render)
 *   - No save-on-keystroke; admin commits via Save button
 *   - Live totals via useMemo
 *   - Mode-switch warning when wisselen data verloren zou doen
 *   - beforeunload guard wanneer er onopgeslagen wijzigingen zijn
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";
import { Save, Calculator, Boxes, Package, AlertTriangle, RotateCcw } from "lucide-react";
import QuoteLineItemsEditor, { EMPTY_LINE } from "./QuoteLineItemsEditor";
import { num, vatOrDefault, computeBasisTotals } from "./quoteBasisHelpers";

const MODE_INFO = {
  total_volume: { Icon: Calculator, label: "Totaalvolume", hint: "Snelle offerte op basis van totaal m³" },
  lines: { Icon: Boxes, label: "Regels / items", hint: "Gedetailleerde offerte met meerdere items" },
  fixed: { Icon: Package, label: "Vaste prijs", hint: "Container, service of forfaitaire offerte" },
};

// -------- Mode toggle (top-level for R4) --------
function ModeToggle({ mode, onChange, disabled }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="qbe-mode-toggle">
      {Object.entries(MODE_INFO).map(([k, info]) => {
        const Icon = info.Icon;
        const active = mode === k;
        return (
          <button
            type="button"
            key={k}
            disabled={disabled}
            onClick={() => onChange(k)}
            className={`text-left rounded-none border-2 p-3 transition-colors ${
              active
                ? "border-[#0055FF] bg-[#0055FF]/5"
                : "border-gray-200 bg-white hover:border-gray-400"
            } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
            data-testid={`qbe-mode-${k}`}
          >
            <div className="flex items-center gap-2">
              <Icon className={`w-4 h-4 ${active ? "text-[#0055FF]" : "text-gray-500"}`} />
              <span className={`font-bold text-sm ${active ? "text-[#0055FF]" : "text-[#0A192F]"}`}>
                {info.label}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-1">{info.hint}</div>
          </button>
        );
      })}
    </div>
  );
}

// -------- Live totals panel (top-level) --------
function TotalsPanel({ totals }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-xs" data-testid="qbe-totals-panel">
      <div className="bg-gray-50 border border-gray-200 px-2 py-1.5">
        <div className="text-[10px] text-gray-500">Subtotaal excl. BTW</div>
        <div className="font-mono font-bold text-sm" data-testid="qbe-derived-subtotal">€ {totals.sub.toFixed(2)}</div>
      </div>
      <div className="bg-gray-50 border border-gray-200 px-2 py-1.5">
        <div className="text-[10px] text-gray-500">BTW ({totals.vatPct}%)</div>
        <div className="font-mono font-bold text-sm" data-testid="qbe-derived-vat">€ {totals.vat.toFixed(2)}</div>
      </div>
      <div className="bg-white border-2 border-[#0055FF] px-2 py-1.5">
        <div className="text-[10px] text-[#0055FF]">Totaal incl. BTW</div>
        <div className="font-mono font-bold text-sm text-[#0055FF]" data-testid="qbe-derived-total">€ {totals.total.toFixed(2)}</div>
      </div>
    </div>
  );
}

// -------- Pricing extras grid (top-level) --------
function PricingExtrasGrid({ pricing, setP, canEdit, showDocFee = true, showPickup = true }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      <div><Label className="text-[10px] uppercase">Administratie</Label><Input data-testid="qbe-admin-fee" type="number" step="0.01" disabled={!canEdit} value={pricing.admin_fee ?? ""} onChange={(e) => setP("admin_fee", e.target.value)} className="rounded-none mt-1" /></div>
      {showDocFee ? <div><Label className="text-[10px] uppercase">Documentatie</Label><Input data-testid="qbe-doc-fee" type="number" step="0.01" disabled={!canEdit} value={pricing.documentation_fee ?? ""} onChange={(e) => setP("documentation_fee", e.target.value)} className="rounded-none mt-1" /></div> : null}
      {showPickup ? <div><Label className="text-[10px] uppercase">Pickup</Label><Input data-testid="qbe-pickup-fee" type="number" step="0.01" disabled={!canEdit} value={pricing.pickup_fee ?? ""} onChange={(e) => setP("pickup_fee", e.target.value)} className="rounded-none mt-1" /></div> : null}
      <div><Label className="text-[10px] uppercase">Extra handling</Label><Input data-testid="qbe-extra-handling" type="number" step="0.01" disabled={!canEdit} value={pricing.extra_handling ?? ""} onChange={(e) => setP("extra_handling", e.target.value)} className="rounded-none mt-1" /></div>
      <div><Label className="text-[10px] uppercase">Korting</Label><Input data-testid="qbe-discount" type="number" step="0.01" disabled={!canEdit} value={pricing.discount ?? ""} onChange={(e) => setP("discount", e.target.value)} className="rounded-none mt-1" /></div>
      <div>
        <Label className="text-[10px] uppercase">BTW % (0/9/21)</Label>
        <Input
          data-testid="qbe-vat-pct"
          type="number" step="0.01" min="0" max="100"
          disabled={!canEdit}
          value={pricing.vat_percent ?? ""}
          onChange={(e) => setP("vat_percent", e.target.value)}
          placeholder="21"
          className="rounded-none mt-1"
        />
      </div>
    </div>
  );
}

// -------- Total Volume mode (top-level) --------
function TotalVolumeMode({ data, setData, canEdit }) {
  const cbm = num(data.total_volume_cbm);
  const rate = num(data.pricing?.price_per_cbm);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      <div>
        <Label className="text-[10px] uppercase">Totaal volume (m³) *</Label>
        <Input data-testid="qbe-tv-cbm" type="number" min="0" step="0.01" disabled={!canEdit} value={data.total_volume_cbm ?? ""}
          onChange={(e) => setData({ ...data, total_volume_cbm: e.target.value })}
          className="rounded-none mt-1 font-mono" />
      </div>
      <div>
        <Label className="text-[10px] uppercase">Tarief per m³ (€) *</Label>
        <Input data-testid="qbe-tv-rate" type="number" min="0" step="0.01" disabled={!canEdit} value={data.pricing?.price_per_cbm ?? ""}
          onChange={(e) => setData({ ...data, pricing: { ...(data.pricing || {}), price_per_cbm: e.target.value } })}
          className="rounded-none mt-1 font-mono" />
      </div>
      <div>
        <Label className="text-[10px] uppercase">Aantal colli/items</Label>
        <Input data-testid="qbe-tv-colli" type="number" min="0" disabled={!canEdit} value={data.shipment?.boxes ?? ""}
          onChange={(e) => setData({ ...data, shipment: { ...(data.shipment || {}), boxes: e.target.value } })}
          className="rounded-none mt-1" />
      </div>
      <div className="bg-[#0055FF]/5 border border-[#0055FF]/30 px-2 py-1.5">
        <div className="text-[10px] text-[#0055FF] uppercase">Volume × tarief</div>
        <div className="font-mono font-bold text-[#0055FF]" data-testid="qbe-tv-base">
          € {(cbm * rate).toFixed(2)}
        </div>
      </div>
    </div>
  );
}

// -------- Fixed mode (top-level) --------
function FixedMode({ data, setData, canEdit }) {
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-[10px] uppercase">Omschrijving service / vaste post *</Label>
        <Input data-testid="qbe-fx-description" disabled={!canEdit} value={data.fixed_description ?? ""}
          onChange={(e) => setData({ ...data, fixed_description: e.target.value })}
          placeholder="Bv. Zeevracht 20ft container Rotterdam → Willemstad"
          className="rounded-none mt-1" />
      </div>
      <div>
        <Label className="text-[10px] uppercase">Bedrag (€) *</Label>
        <Input data-testid="qbe-fx-amount" type="number" min="0" step="0.01" disabled={!canEdit} value={data.fixed_amount ?? ""}
          onChange={(e) => setData({ ...data, fixed_amount: e.target.value })}
          className="rounded-none mt-1 font-mono" />
      </div>
    </div>
  );
}

// -------- Mode-switch warning (top-level) --------
function ModeSwitchWarning({ show, pendingMode, onConfirm, onCancel }) {
  if (!show) return null;
  return (
    <div className="border-2 border-amber-400 bg-amber-50 p-3 flex items-start gap-2 mt-2" data-testid="qbe-mode-switch-warning">
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-sm">
        <p className="font-bold text-amber-900">Modus wisselen naar "{MODE_INFO[pendingMode]?.label}" verwijdert ingevoerde gegevens.</p>
        <p className="text-amber-800 text-xs mt-1">U heeft gegevens in de huidige modus. Doorgaan zal die wissen — uw klant- en zendinggegevens blijven behouden.</p>
        <div className="mt-2 flex gap-2">
          <Button type="button" size="sm" onClick={onConfirm} className="rounded-none bg-amber-600 hover:bg-amber-700 text-white h-7" data-testid="qbe-mode-switch-confirm">
            Toch wisselen
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={onCancel} className="rounded-none h-7" data-testid="qbe-mode-switch-cancel">
            Annuleren
          </Button>
        </div>
      </div>
    </div>
  );
}

// -------- Legacy migration banner (top-level) --------
function LegacyMigrationBanner({ canEdit, onMigrate }) {
  return (
    <div className="border-2 border-blue-300 bg-blue-50 p-4" data-testid="qbe-legacy-banner">
      <div className="flex items-start gap-3">
        <RotateCcw className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-bold text-blue-900 text-sm">Deze offerte gebruikt de oude single-shipment layout.</p>
          <p className="text-xs text-blue-800 mt-1">
            U kunt hem omzetten naar één van de drie nieuwe modi zodat berekeningen automatisch lopen.
            Originele zending- en prijsdata blijft behouden tot u opslaat.
          </p>
          {canEdit ? (
            <div className="flex flex-wrap gap-2 mt-3">
              <Button size="sm" type="button" onClick={() => onMigrate("total_volume")} className="rounded-none bg-blue-700 hover:bg-blue-800 text-white" data-testid="qbe-migrate-total_volume">
                <Calculator className="w-3.5 h-3.5 mr-1" /> Naar Totaalvolume
              </Button>
              <Button size="sm" type="button" variant="outline" onClick={() => onMigrate("lines")} className="rounded-none border-blue-700 text-blue-700 hover:bg-blue-100" data-testid="qbe-migrate-lines">
                <Boxes className="w-3.5 h-3.5 mr-1" /> Naar Regels/items
              </Button>
              <Button size="sm" type="button" variant="outline" onClick={() => onMigrate("fixed")} className="rounded-none border-blue-700 text-blue-700 hover:bg-blue-100" data-testid="qbe-migrate-fixed">
                <Package className="w-3.5 h-3.5 mr-1" /> Naar Vaste prijs
              </Button>
            </div>
          ) : (
            <p className="text-xs text-gray-500 mt-2">Alleen gebruikers met bewerken-rechten kunnen migreren.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// MAIN COMPONENT
// =========================================================================
export default function QuoteBasisEditor({ quote, canEdit, onSaved }) {
  // Local working state — initial seeded from server data.
  const initialState = () => {
    const lines = (quote?.line_items || []).map((l, i) => ({ ...l, _lid: `srv_${i}_${Math.random().toString(36).slice(2, 6)}` }));
    return {
      quote_basis: quote?.quote_basis || null,
      line_items: lines,
      total_volume_cbm: quote?.total_volume_cbm ?? "",
      fixed_amount: quote?.fixed_amount ?? "",
      fixed_description: quote?.fixed_description ?? "",
      customer_note: quote?.customer_note ?? "",
      shipment: { ...(quote?.shipment || {}) },
      pricing: { ...(quote?.pricing || {}) },
    };
  };

  const [state, setState] = useState(initialState);
  const baselineRef = useRef(JSON.stringify(initialState()));
  const [presets, setPresets] = useState([]);
  const [pendingMode, setPendingMode] = useState(null);
  const [saving, setSaving] = useState(false);

  // Re-seed when the parent loads a new quote (different id).
  useEffect(() => {
    const fresh = initialState();
    setState(fresh);
    baselineRef.current = JSON.stringify(fresh);
    setPendingMode(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id]);

  // Load presets on mount.
  useEffect(() => {
    adminApi.get("/admin/quotes-v2/_meta/line-presets")
      .then((r) => setPresets(r.data?.presets || []))
      .catch(() => setPresets([]));
  }, []);

  const isDirty = JSON.stringify(state) !== baselineRef.current;

  // beforeunload guard — alleen bij actieve dirty state.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const totals = useMemo(
    () => computeBasisTotals(state.quote_basis, state),
    [state],
  );

  // Mode-switch handling
  const hasModeData = () => {
    if (state.quote_basis === "lines") return (state.line_items || []).length > 0;
    if (state.quote_basis === "total_volume")
      return num(state.total_volume_cbm) > 0 || num(state.pricing?.price_per_cbm) > 0;
    if (state.quote_basis === "fixed")
      return num(state.fixed_amount) > 0 || (state.fixed_description || "").trim().length > 0;
    return false;
  };

  const requestModeChange = (next) => {
    if (next === state.quote_basis) return;
    if (hasModeData()) setPendingMode(next);
    else applyModeChange(next);
  };
  const applyModeChange = (next) => {
    setState((s) => ({
      ...s,
      quote_basis: next,
      // wipe other-mode payload only — klant/zending/extras blijven
      line_items: next === "lines" ? s.line_items : [],
      total_volume_cbm: next === "total_volume" ? s.total_volume_cbm : "",
      fixed_amount: next === "fixed" ? s.fixed_amount : "",
      fixed_description: next === "fixed" ? s.fixed_description : "",
    }));
    setPendingMode(null);
  };
  const confirmSwitch = () => { if (pendingMode) applyModeChange(pendingMode); };
  const cancelSwitch = () => setPendingMode(null);

  // Legacy migration: seed proper starter data per mode
  const onLegacyMigrate = (target) => {
    const shipCbm = num(quote?.shipment?.cbm);
    const shipRate = num(quote?.pricing?.price_per_cbm);
    setState((s) => {
      const next = { ...s, quote_basis: target };
      if (target === "lines") {
        next.line_items = [{
          ...EMPTY_LINE(),
          description: quote?.shipment?.shipment_type || "Bestaande zending",
          quantity: num(quote?.shipment?.boxes) || 1,
          length_cm: num(quote?.shipment?.length_cm) || "",
          width_cm: num(quote?.shipment?.width_cm) || "",
          height_cm: num(quote?.shipment?.height_cm) || "",
          cbm: shipCbm || "",
          weight_kg: num(quote?.shipment?.weight_kg) || "",
          rate: shipRate || "",
          line_type: shipCbm ? "cbm" : "qty",
        }];
      } else if (target === "total_volume") {
        next.total_volume_cbm = shipCbm || "";
        next.pricing = { ...(next.pricing || {}), price_per_cbm: shipRate || next.pricing?.price_per_cbm };
      } else if (target === "fixed") {
        next.fixed_description = quote?.description || quote?.shipment?.description || "Service";
        next.fixed_amount = num(quote?.pricing?.subtotal_excl_vat) || "";
      }
      return next;
    });
  };

  const setPricing = (k, v) => setState((s) => ({ ...s, pricing: { ...(s.pricing || {}), [k]: v } }));

  const validate = () => {
    if (state.quote_basis === "lines") {
      const valid = (state.line_items || []).filter((l) => (l.description || "").trim() || num(l.rate) > 0);
      if (valid.length === 0) return "Vul minimaal één geldige offerteregel in.";
    } else if (state.quote_basis === "total_volume") {
      if (num(state.total_volume_cbm) <= 0) return "Totaal volume (m³) is verplicht.";
      if (num(state.pricing?.price_per_cbm) <= 0) return "Tarief per m³ is verplicht.";
    } else if (state.quote_basis === "fixed") {
      if (!(state.fixed_description || "").trim()) return "Vul de omschrijving in voor de vaste prijs.";
      if (num(state.fixed_amount) <= 0) return "Bedrag is verplicht voor vaste prijs.";
    }
    if (num(state.pricing?.vat_percent) < 0) return "BTW-percentage mag niet negatief zijn.";
    return null;
  };

  const buildPayload = () => {
    const payload = {
      quote_basis: state.quote_basis,
      pricing: {
        ...(state.pricing || {}),
        // BTW 0% fix: vatOrDefault returns 21 alleen op echt-leeg, 0 wordt 0.
        vat_percent: vatOrDefault(state.pricing?.vat_percent),
      },
      customer_note: state.customer_note || null,
    };
    if (state.quote_basis === "lines") {
      payload.line_items = (state.line_items || [])
        .filter((l) => (l.description || "").trim() || num(l.rate) > 0)
        .map((l) => ({
          description: l.description || null,
          quantity: num(l.quantity) || 1,
          length_cm: num(l.length_cm) || null,
          width_cm: num(l.width_cm) || null,
          height_cm: num(l.height_cm) || null,
          cbm: num(l.cbm) || null,
          weight_kg: num(l.weight_kg) || null,
          rate: num(l.rate),
          note: l.note || null,
          line_type: l.line_type || "cbm",
          preset_key: l.preset_key || null,
        }));
    } else if (state.quote_basis === "total_volume") {
      payload.total_volume_cbm = num(state.total_volume_cbm);
      payload.pricing.price_per_cbm = num(state.pricing?.price_per_cbm) || null;
      if (state.shipment?.boxes !== undefined && state.shipment?.boxes !== "") {
        payload.shipment = { ...(quote?.shipment || {}), boxes: num(state.shipment.boxes) || null };
      }
    } else if (state.quote_basis === "fixed") {
      payload.fixed_amount = num(state.fixed_amount);
      payload.fixed_description = state.fixed_description;
    }
    // Numeric extras: store as float or null (avoid empty-string into DB).
    ["admin_fee", "documentation_fee", "pickup_fee", "callout_fee", "km_fee", "extra_handling", "discount"]
      .forEach((k) => {
        if (payload.pricing[k] === "" || payload.pricing[k] === undefined) payload.pricing[k] = null;
        else if (payload.pricing[k] !== null) payload.pricing[k] = num(payload.pricing[k]);
      });
    return payload;
  };

  const onSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const r = await adminApi.put(`/admin/quotes-v2/${quote.id}`, buildPayload());
      toast.success("Offerte opgeslagen.");
      const fresh = {
        quote_basis: r.data?.quote_basis || null,
        line_items: (r.data?.line_items || []).map((l, i) => ({ ...l, _lid: `srv_${i}_${Math.random().toString(36).slice(2, 6)}` })),
        total_volume_cbm: r.data?.total_volume_cbm ?? "",
        fixed_amount: r.data?.fixed_amount ?? "",
        fixed_description: r.data?.fixed_description ?? "",
        customer_note: r.data?.customer_note ?? "",
        shipment: { ...(r.data?.shipment || {}) },
        pricing: { ...(r.data?.pricing || {}) },
      };
      setState(fresh);
      baselineRef.current = JSON.stringify(fresh);
      onSaved?.(r.data);
    } catch (err2) {
      toast.error(formatApiError(err2));
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => {
    if (!isDirty) return;
    if (window.confirm("Wijzigingen verwerpen?")) {
      const fresh = initialState();
      setState(fresh);
      setPendingMode(null);
    }
  };

  // ============ RENDER ============

  // Legacy quote without basis → banner + (parent renders the legacy form below)
  if (!state.quote_basis) {
    return <LegacyMigrationBanner canEdit={canEdit} onMigrate={onLegacyMigrate} />;
  }

  return (
    <div className="space-y-5" data-testid="quote-basis-editor">
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-chivo font-black text-sm text-[#0055FF] uppercase tracking-wide">
            Offertebasis
          </h3>
          {isDirty ? (
            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5" data-testid="qbe-dirty-indicator">
              Onopgeslagen wijzigingen
            </span>
          ) : null}
        </div>
        <ModeToggle mode={state.quote_basis} onChange={requestModeChange} disabled={!canEdit} />
        <ModeSwitchWarning show={!!pendingMode} pendingMode={pendingMode} onConfirm={confirmSwitch} onCancel={cancelSwitch} />
      </section>

      <section data-testid={`qbe-section-${state.quote_basis}`}>
        <h3 className="font-chivo font-black text-sm text-[#0055FF] uppercase tracking-wide mb-2">
          {state.quote_basis === "lines" ? "Offerteregels" : state.quote_basis === "total_volume" ? "Volume & tarief" : "Vaste prijs"}
        </h3>
        {state.quote_basis === "total_volume" ? (
          <TotalVolumeMode data={state} setData={setState} canEdit={canEdit} />
        ) : null}
        {state.quote_basis === "fixed" ? (
          <FixedMode data={state} setData={setState} canEdit={canEdit} />
        ) : null}
        {state.quote_basis === "lines" ? (
          <QuoteLineItemsEditor
            lines={state.line_items}
            onChange={canEdit ? (next) => setState((s) => ({ ...s, line_items: next })) : () => {}}
            presets={presets}
          />
        ) : null}
      </section>

      <section>
        <h3 className="font-chivo font-black text-sm text-[#0055FF] uppercase tracking-wide mb-2">
          Extra kosten & BTW
        </h3>
        <PricingExtrasGrid
          pricing={state.pricing || {}}
          setP={setPricing}
          canEdit={canEdit}
          showDocFee={state.quote_basis !== "fixed"}
          showPickup={state.quote_basis !== "fixed"}
        />
        <div className="mt-3">
          <TotalsPanel totals={totals} />
        </div>
      </section>

      <section>
        <Label className="text-[10px] uppercase">Klantnotitie (zichtbaar in PDF)</Label>
        <Textarea data-testid="qbe-customer-note" disabled={!canEdit} value={state.customer_note ?? ""}
          onChange={(e) => setState((s) => ({ ...s, customer_note: e.target.value }))}
          className="rounded-none mt-1 min-h-[60px]" />
      </section>

      {canEdit ? (
        <div className="flex items-center gap-2 sticky bottom-0 bg-white border-t border-gray-200 -mx-6 px-6 py-3 z-10" data-testid="qbe-actions-bar">
          <Button
            type="button"
            onClick={onSave}
            disabled={saving || !isDirty}
            className="rounded-none bg-[#0055FF] hover:bg-[#0044CC] text-white font-chivo font-bold uppercase text-xs"
            data-testid="qbe-save"
          >
            <Save className="w-4 h-4 mr-1" /> {saving ? "Opslaan…" : "Opslaan wijzigingen"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onReset}
            disabled={!isDirty || saving}
            className="rounded-none"
            data-testid="qbe-reset"
          >
            <RotateCcw className="w-4 h-4 mr-1" /> Wijzigingen verwerpen
          </Button>
          {isDirty ? (
            <span className="text-xs text-gray-500 ml-2">
              Berekeningen worden server-side opnieuw uitgevoerd bij opslaan.
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
